<template>
  <div
    class="d-flex"
    :class="{ 'justify-end': !isShowMode }"
  >
    <div class="d-flex flex-column">
      <div
        v-if="Boolean(fileList.length)"
        class="file-list"
        :class="{
          'align-start': isShowMode,
          'align-end': !isShowMode
        }"
      >
        <div
          class="d-flex align-center"
          v-for="(file, index) of fileList"
          :key="index + 'file'"
        >
          <div
            class="text-center"
            v-if="downLoadLoading && index === selectedFileIndex && !isShowMode"
          >
            <v-progress-circular
              :size="18"
              color="primary"
              width="2"
              indeterminate
            />
          </div>

          <v-btn
            class="pa-0 mr-1 d-flex align-center simple-button file-button"
            :class="{
              'file-button-left': isShowMode,
              'ml-0': isShowMode,
              'ml-1': !isShowMode
            }"
            :disabled="(downLoadLoading || removeLoading) && index === selectedFileIndex"
            color="primary"
            plain
            :ripple="false"
            @click="downloadFile(file, index)"
          >
            <span class="btn-text">
              <v-icon
                class="uikit-icon paperclip file-icon size-16 left-position"
                color="primary"
              />
              <div class="ml-4">{{ file.id ? file.originalName : file.name }}</div>
            </span>
          </v-btn>

          <div
            class="text-center"
            v-if="downLoadLoading && index === selectedFileIndex && isShowMode"
          >
            <v-progress-circular
              :size="18"
              color="primary"
              width="2"
              indeterminate
            />
          </div>

          <div
            class="text-center"
            v-if="removeLoading && index === selectedFileIndex"
          >
            <v-progress-circular
              :size="18"
              color="error"
              width="2"
              indeterminate
            />
          </div>

          <v-icon
            v-else-if="!isShowMode"
            :disabled="(downLoadLoading || removeLoading) && index === selectedFileIndex"
            class="uikit-icon trash size-18 remove-btn"
            title="Удалить"
            @click="removeFile(file, index)"
          />
        </div>
      </div>
      <v-btn
        v-if="!isShowMode"
        ref="uploadButton"
        outlined
        small
        height="32"
        color="primary"
        class="mt-2 action-btn elevation-0 rounded-lg align-self-end"
        :disabled="buttonDisabled"
        :title="buttonDisableTitle"
        @click="resetErrorMessages(), $refs.inputFile.click()"
      >
        <input
          type="file"
          class="input-file"
          ref="inputFile"
          :accept="acceptFileFormats"
          @change="uploadLinkFile"
          @cancel="uploadLinkFile"
          :multiple="multiple"
        />
        <v-icon class="uikit-icon docplus primary size-20" />
        <span class="btn-text ml-1"> {{ buttonText }} </span>
      </v-btn>
      <transition name="fade">
        <div
          v-if="Boolean(errorMessages.length)"
          class="pl-4 text-error-message align-self-end"
          :style="{ width: widthErrorMessage }"
        >
          {{ errorMessages[0] }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import download from '@/plugins/download'
import { fileApi } from '@/api'

export default {
  name: 'UIUploadFile',

  props: {
    maxFileWeightInMb: {
      type: Number,
      default: 5
    },
    acceptFileFormats: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: Number,
      default: 10
    },
    fileList: {
      type: Array,
      default() {
        return []
      }
    },
    isShowMode: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: 'Добавить файлы'
    }
  },

  data: () => ({
    selectedFileIndex: null,
    errorMessages: [],
    downLoadLoading: false,
    removeLoading: false,
    widthErrorMessage: '340px',
    calculateWidthErrorMessage: false,

    fileTypes: {
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.pdf': 'application/pdf',
      '.jpeg': 'image/jpeg',
      '.jpg': 'image/jpeg'
    }
  }),

  computed: {
    buttonDisableTitle() {
      return this.buttonDisabled ? `Не более ${this.maxFiles} файлов` : ''
    },

    buttonDisabled() {
      return this.fileList.length >= this.maxFiles
    }
  },

  methods: {
    resetErrorMessages() {
      this.errorMessages = []
    },

    async downloadFileFromAPI(file, index) {
      try {
        this.selectedFileIndex = index
        this.downLoadLoading = true
        const fileFromAPI = await fileApi.getFile(file.id)
        download(fileFromAPI, file.originalName)
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось получить файл')
      } finally {
        this.selectedFileIndex = null
        this.downLoadLoading = false
      }
    },

    downloadFile(file, index) {
      if (file.id) {
        this.downloadFileFromAPI(file, index)
      } else {
        download(file, file.name)
      }
    },

    removeFile(file, index) {
      this.$emit('removeFile', index)
    },

    uploadLinkFile(e) {
      if (!e.target?.files?.length) return

      if (!this.calculateWidthErrorMessage) {
        const uploadButtonWidth = this.$refs.uploadButton.$el.getBoundingClientRect()?.width
        this.widthErrorMessage = uploadButtonWidth ? Math.trunc(uploadButtonWidth) + 'px' : this.widthErrorMessage
        this.calculateWidthErrorMessage = true
      }

      if (e.target?.files?.length > this.maxFiles - this.fileList.length) {
        this.errorMessages.push(`Не более ${this.maxFiles} файлов`)
        return
      }

      const maxFileWeightInBytes = this.maxFileWeightInMb * 1024 * 1024

      for (const file of e.target.files) {
        if (file.size > maxFileWeightInBytes) {
          this.errorMessages.push(`Размер загружаемого файла должен быть не более ${this.maxFileWeightInMb}Мб`)
          return
        }

        if (
          this.acceptFileFormats &&
          !this.acceptFileFormats
            .split(',')
            .map(el => this.fileTypes[el.trim()])
            .includes(file.type)
        ) {
          this.errorMessages.push(`Расширение файла должно быть ${this.acceptFileFormats}`)
          return
        }
      }

      this.$emit('uploadFiles', this.maxFiles === 1 ? e.target.files[0] : e.target.files)
    }
  }
}
</script>

<style scoped>
.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-file {
  width: 0px;
  opacity: 0;
  position: absolute;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.remove-btn:hover {
  background: var(--v-error-base) !important;
}

.file-button {
  height: auto !important;
  min-height: 22px !important;
}

.file-button .btn-text {
  max-width: 400px;
  word-break: break-all;
  white-space: normal;
  overflow-wrap: break-word;

  text-align: right;
}

.file-button-left .btn-text {
  text-align: left;
}

.file-icon {
  background: var(--v-primary-base) !important;
}
</style>
